import { sxAppend } from '@/libs/material/sx';
import {
    DS_HEIGHT,
    DS_SHAPE_SPACING,
    DS_SPACING_UNITS,
} from '@calm/evexia-design-tokens';
import commonMessages from '@rhg/lang/messages/common';
import { Button, SxProps, Theme } from '@rhg/libs/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

const padding = {
    xs: 4 * DS_SPACING_UNITS.SIDE_SMALL,
    sm: 4 * DS_SPACING_UNITS.SIDE_SMALL,
    md: 4 * DS_SPACING_UNITS.SIDE_MEDIUM,
    lg: 4 * DS_SPACING_UNITS.SIDE_LARGE,
};

const styleAccessibilityButton = {
    zIndex: (theme: Theme) => theme.zIndex.drawer + 1,
    position: 'fixed',
    right: -1000,
    borderRadius: 0,
    top: `${DS_HEIGHT.TOOLBAR + DS_SHAPE_SPACING.BASE}px`,
    ['&:focus']: {
        right: 0,
    },
};

const styleHideControlsButtonUpperArea = (actionsCount: number) => {
    // Actions total width plus their between margin
    const actionsWidth = actionsCount * 48 + (actionsCount - 1) * 16;

    return {
        zIndex: (theme: Theme) => theme.zIndex.modal + 1,
        position: 'absolute',
        top: 0,
        left: {
            xs: 48 + padding.xs,
            sm: 48 + padding.sm,
            md: 48 + padding.md,
            lg: 48 + padding.lg,
        },
        right: {
            xs: actionsWidth + padding.xs,
            sm: actionsWidth + padding.sm,
            md: actionsWidth + padding.md,
            lg: actionsWidth + padding.lg,
        },
        // This calculation accounts for the height of the top details section before the rating CTAs
        height: {
            xs: 7 * 48 + padding.xs,
            sm: 7 * 48 + padding.sm,
            md: 7 * 48 + padding.md,
            lg: 7 * 48 + padding.lg,
        },
        borderRadius: 0,
        cursor: 'default',
        opacity: 0,
    };
};

const styleHideControlsButtonArea = (
    isWholeAreaClickable: boolean,
    hasDialogBottomAction: boolean
) => ({
    zIndex: (theme: Theme) => theme.zIndex.modal + 1,
    width: '100%',
    position: 'absolute',
    top: isWholeAreaClickable
        ? 0
        : // We want this area to start below the top actions. The actions have
          // height of 48px, and their padding varies based on the screen width.
          {
              xs: 48 + padding.xs,
              sm: 48 + padding.sm,
              md: 48 + padding.md,
              lg: 48 + padding.lg,
          },
    height: isWholeAreaClickable
        ? '100%'
        : // We want the height of this area to equal the height of the page minus
          // the height of the upper actions (48px), minus the bottom controls height (depends on the screen width)
          // minus the page top and bottom padding.
          // the DialogBottomAction factors in the top padding
          {
              xs: `calc(100% - ${
                  2 * padding.xs + 48 + (hasDialogBottomAction ? 56 : 0) + 100
              }px)`,
              sm: `calc(100% - ${
                  2 * padding.sm + (hasDialogBottomAction ? 56 : 0) + 48 + 100
              }px)`,
              md: `calc(100% - ${
                  2 * padding.md + (hasDialogBottomAction ? 56 : 0) + 48 + 94
              }px)`,
              lg: `calc(100% - ${
                  2 * padding.lg + (hasDialogBottomAction ? 56 : 0) + 48 + 78
              }px)`,
          },
    borderRadius: 0,
    cursor: 'default',
    opacity: 0,
});

interface ToggleControlsAreaProps {
    actionsCount: number;
    areControlsHidden: boolean;
    hasDialogBottomAction?: boolean;
    onToggle: () => void;
    sx?: SxProps<Theme>;
}

/**
 * We want this area to cover the whole page, except for the area covered by the
 * upper actions and bottom controls.
 */
export function ToggleControlsArea({
    actionsCount,
    areControlsHidden,
    onToggle,
    hasDialogBottomAction = false,
    sx,
}: ToggleControlsAreaProps) {
    const { formatMessage } = useIntl();

    const controlsToggleAreaStyle = useMemo(
        () =>
            styleHideControlsButtonArea(
                areControlsHidden,
                hasDialogBottomAction
            ),
        [areControlsHidden, hasDialogBottomAction]
    );

    const controlsToggleUpperAreaStyle = useMemo(
        () => styleHideControlsButtonUpperArea(actionsCount),
        [actionsCount]
    );

    const label = useMemo(
        () =>
            areControlsHidden
                ? formatMessage(commonMessages['common.show_controls'])
                : formatMessage(commonMessages['common.hide_controls']),
        [areControlsHidden, formatMessage]
    );

    return (
        <>
            {/* This button covers the area between the upper actions. We want this to
                render only when the controls are displayed, because when the controls
                are hidden, the button below covers the whole page.
            */}
            {!areControlsHidden && (
                <Button
                    aria-hidden
                    skipTracking
                    onClick={onToggle}
                    sx={controlsToggleUpperAreaStyle}
                />
            )}

            <Button
                aria-hidden
                skipTracking
                onClick={onToggle}
                sx={sxAppend(controlsToggleAreaStyle, sx)}
            />

            {/* For screen readers */}
            <Button
                sx={styleAccessibilityButton}
                skipTracking
                variant="contained"
                color="secondary"
                onClick={onToggle}
            >
                {label}
            </Button>
        </>
    );
}
